import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { TableKit } from '@tiptap/extension-table';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, List, ListOrdered,
  LinkIcon, Image as ImageIcon, Table as TableIcon, Undo, Redo, Trash2,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import MediaPickerModal from '@/components/MediaPickerModal';
import StoryImage, { MAX_STORY_IMAGES, countStoryImages } from '@/components/StoryImageNode';

const GRID_MAX = 6;

function ToolbarButton({ onClick, active, disabled, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded transition-colors disabled:opacity-35 disabled:cursor-not-allowed ${
        active
          ? 'bg-[#1F1A17] text-[#FFE4C4]'
          : 'text-[#6E6258] hover:bg-[#FFE4C4] hover:text-[#1F1A17]'
      }`}
    >
      {children}
    </button>
  );
}

function TableGrid({ onPick }) {
  const [hover, setHover] = useState({ rows: 0, cols: 0 });

  return (
    <div
      className="absolute left-0 top-full mt-1 z-50 rounded-lg border border-border bg-white shadow-lg p-2"
      onMouseLeave={() => setHover({ rows: 0, cols: 0 })}
    >
      <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${GRID_MAX}, 18px)` }}>
        {Array.from({ length: GRID_MAX * GRID_MAX }).map((_, i) => {
          const r = Math.floor(i / GRID_MAX) + 1;
          const c = (i % GRID_MAX) + 1;
          const on = r <= hover.rows && c <= hover.cols;
          return (
            <button
              key={i}
              type="button"
              onMouseEnter={() => setHover({ rows: r, cols: c })}
              onClick={() => onPick(r, c)}
              className={`h-[18px] w-[18px] rounded-[2px] border transition-colors ${
                on ? 'bg-[#1F1A17] border-[#1F1A17]' : 'bg-white border-[#E7D2B8] hover:border-[#C9B49A]'
              }`}
            />
          );
        })}
      </div>
      <p className="text-[11px] text-[#9C8E84] text-center mt-2 tabular-nums">
        {hover.rows > 0 ? `${hover.rows} × ${hover.cols}` : 'Pick a size'}
      </p>
    </div>
  );
}

function TableControls({ editor }) {
  const btn = 'px-2 py-1 rounded text-[11px] font-medium text-[#6E6258] hover:bg-[#FFE4C4] hover:text-[#1F1A17] transition-colors';
  const run = (fn) => () => fn(editor.chain().focus()).run();

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border bg-[#FFF9F0] flex-wrap">
      <span className="text-[10px] uppercase tracking-wide font-semibold text-[#9C8E84] mr-1.5">Table</span>
      <button type="button" className={btn} onClick={run(c => c.addRowBefore())}>Row above</button>
      <button type="button" className={btn} onClick={run(c => c.addRowAfter())}>Row below</button>
      <button type="button" className={btn} onClick={run(c => c.deleteRow())}>Delete row</button>
      <span className="w-px h-4 bg-[#E7D2B8] mx-1" />
      <button type="button" className={btn} onClick={run(c => c.addColumnBefore())}>Column left</button>
      <button type="button" className={btn} onClick={run(c => c.addColumnAfter())}>Column right</button>
      <button type="button" className={btn} onClick={run(c => c.deleteColumn())}>Delete column</button>
      <span className="w-px h-4 bg-[#E7D2B8] mx-1" />
      <button type="button" className={btn} onClick={run(c => c.toggleHeaderRow())}>Header row</button>
      <button
        type="button"
        className="px-2 py-1 rounded text-[11px] font-medium text-red-500 hover:bg-red-50 transition-colors inline-flex items-center gap-1"
        onClick={run(c => c.deleteTable())}
      >
        <Trash2 size={11} /> Delete table
      </button>
    </div>
  );
}

function loadDimensions(src) {
  return new Promise(resolve => {
    if (!src) { resolve({ width: null, height: null }); return; }
    const img = new window.Image();
    img.onload  = () => resolve({ width: img.naturalWidth || null, height: img.naturalHeight || null });
    img.onerror = () => resolve({ width: null, height: null });
    img.src = src;
  });
}

const escapeCell = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');
function tsvToTableHtml(text) {
  const rows = text.replace(/\r/g, '').split('\n').filter(r => r.trim().length > 0);
  if (rows.length < 2) return null;
  const cells = rows.map(r => r.split('\t'));
  if (!cells.some(r => r.length > 1)) return null;
  const cols = Math.max(...cells.map(r => r.length));
  const body = cells.map((row, i) => {
    const tag = i === 0 ? 'th' : 'td';
    const tds = Array.from({ length: cols }, (_, c) => `<${tag}>${escapeCell(row[c] ?? '')}</${tag}>`);
    return `<tr>${tds.join('')}</tr>`;
  }).join('');
  return `<table><tbody>${body}</tbody></table>`;
}

export default function TipTapEditor({ content, onChange, placeholder, mediaContext = {} }) {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [mediaOpen, setMediaOpen] = useState(false);
  const [replacePos, setReplacePos] = useState(null);
  const [showTableGrid, setShowTableGrid] = useState(false);
  const requestMediaRef = useRef(null);
  const editorRef = useRef(null);
  const tableBtnRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-blue-600 underline' } }),
      TableKit.configure({
        table: { resizable: false, HTMLAttributes: { class: 'story-table' } },
      }),
      StoryImage.configure({
        onRequestReplace: (pos) => requestMediaRef.current?.(pos),
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        'data-placeholder': placeholder || 'Start writing your story...',
      },
      handlePaste: (view, event) => {
        const clip = event.clipboardData;
        if (!clip) return false;
        const html = clip.getData('text/html');
        if (html && /<table/i.test(html)) return false;
        const text = clip.getData('text/plain');
        if (!text || !text.includes('\t')) return false;
        const tableHtml = tsvToTableHtml(text);
        if (!tableHtml) return false;
        event.preventDefault();
        editorRef.current?.chain().focus().insertContent(tableHtml).run();
        return true;
      },
    },
  });

  editorRef.current = editor;

  requestMediaRef.current = (pos) => {
    setReplacePos(typeof pos === 'number' ? pos : null);
    setMediaOpen(true);
  };

  useEffect(() => {
    if (!showTableGrid) return;
    const onDown = (e) => {
      if (tableBtnRef.current && !tableBtnRef.current.contains(e.target)) setShowTableGrid(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowTableGrid(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showTableGrid]);

  if (!editor) return null;

  const imageCount = countStoryImages(editor);
  const atImageLimit = imageCount >= MAX_STORY_IMAGES;
  const inTable = editor.isActive('table');

  const addLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
      setLinkUrl('');
    }
    setShowLinkInput(false);
  };

  const openImagePicker = () => {
    if (atImageLimit) return;
    setReplacePos(null);
    setMediaOpen(true);
  };

  const insertTable = (rows, cols) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    setShowTableGrid(false);
  };

  const handleMediaSelect = async ({ file_url, media_asset_id, caption, alt_text, accreditation }) => {
    const { width, height } = await loadDimensions(file_url);
    const attrs = {
      src:     file_url,
      alt:     alt_text || '',
      caption: caption || '',
      credit:  accreditation || '',
      assetId: media_asset_id || null,
      width,
      height,
    };

    if (replacePos !== null) {
      const target = replacePos;
      editor.chain().focus().command(({ tr }) => {
        const node = tr.doc.nodeAt(target);
        if (!node || node.type.name !== 'storyImage') return false;
        tr.setNodeMarkup(target, undefined, { ...node.attrs, ...attrs });
        return true;
      }).run();
    } else {
      editor.chain().focus().insertContent({ type: 'storyImage', attrs }).run();
    }

    setReplacePos(null);
  };

  return (
    <>
      <div className="tiptap-editor bg-white rounded-lg border border-border overflow-hidden" data-testid="tiptap-editor">
        {/* Toolbar */}
        <div className="flex items-center gap-0.5 p-2 border-b border-border bg-[#FFF4E8] flex-wrap">
          <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
            <Bold size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
            <Italic size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
            <UnderlineIcon size={16} />
          </ToolbarButton>
          <div className="w-px h-5 bg-[#E7D2B8] mx-1" />
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">
            <Heading1 size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
            <Heading2 size={16} />
          </ToolbarButton>
          <div className="w-px h-5 bg-[#E7D2B8] mx-1" />
          <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
            <List size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List">
            <ListOrdered size={16} />
          </ToolbarButton>
          <div className="w-px h-5 bg-[#E7D2B8] mx-1" />
          <ToolbarButton onClick={() => setShowLinkInput(!showLinkInput)} active={editor.isActive('link')} title="Insert Link">
            <LinkIcon size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={openImagePicker}
            disabled={atImageLimit}
            title={atImageLimit ? `Limit of ${MAX_STORY_IMAGES} images reached` : 'Insert image at cursor'}
          >
            <ImageIcon size={16} />
          </ToolbarButton>
          <div className="relative" ref={tableBtnRef}>
            <ToolbarButton
              onClick={() => setShowTableGrid(v => !v)}
              active={inTable || showTableGrid}
              title="Insert table"
            >
              <TableIcon size={16} />
            </ToolbarButton>
            {showTableGrid && <TableGrid onPick={insertTable} />}
          </div>
          {imageCount > 0 && (
            <span className={`text-[10px] ml-1 ${atImageLimit ? 'text-amber-600' : 'text-[#9C8E84]'}`}>
              {imageCount}/{MAX_STORY_IMAGES}
            </span>
          )}
          <div className="w-px h-5 bg-[#E7D2B8] mx-1" />
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo">
            <Undo size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo">
            <Redo size={16} />
          </ToolbarButton>
          {showLinkInput && (
            <div className="flex items-center gap-2 ml-2">
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://..."
                className="h-7 px-2 text-sm border border-border rounded text-[#1F1A17] bg-white focus:outline-none focus:ring-1 focus:ring-[#1F1A17]"
                onKeyDown={(e) => e.key === 'Enter' && addLink()}
                data-testid="link-input"
              />
              <button onClick={addLink} className="text-xs px-2 py-1 bg-[#1F1A17] text-[#FFE4C4] rounded hover:bg-[#2B241F]">Set</button>
              <button onClick={() => { setShowLinkInput(false); editor.chain().focus().unsetLink().run(); }} className="text-xs px-2 py-1 text-[#6E6258] hover:text-[#1F1A17]">Remove</button>
            </div>
          )}
        </div>

        {inTable && <TableControls editor={editor} />}

        <EditorContent editor={editor} />
      </div>

      <MediaPickerModal
        open={mediaOpen}
        onOpenChange={(v) => { setMediaOpen(v); if (!v) setReplacePos(null); }}
        organisationId={mediaContext.organisationId}
        organisationName={mediaContext.organisationName}
        personId={mediaContext.personId}
        personName={mediaContext.personName}
        tags={mediaContext.tags}
        publicationId={mediaContext.publicationId}
        organisations={mediaContext.organisations}
        onSelect={handleMediaSelect}
      />
    </>
  );
}